/**
 * Custom HorizontalBarChartComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';
import { HorizontalBarChartComponent as ParentHorizontalBarChartComponent } from '@shared-lib/chart-components/horizontal-bar-chart/horizontal-bar-chart.component';
import { DataErrorComponent } from '@shared-lib/global-components/data-error/data-error.component';
import { CapitalCasePipe } from '@shared-lib/pipes/capital-case.pipe';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { formatDecimalWithCommas } from '@shared-lib/utils/format.utils';

@Component({
  selector: 'app-horizontal-bar-chart',
  standalone: true,
  imports: [CommonModule, CapitalCasePipe, DataErrorComponent],
  templateUrl: './horizontal-bar-chart.component.html',
  styleUrls: ['./horizontal-bar-chart.component.scss']
})
export class HorizontalBarChartComponent extends ParentHorizontalBarChartComponent {
  private customConfigService = inject(ConfigService);
  // Constructor is inherited from parent to avoid dependency injection issues

  protected override loadAndRenderChart(filters?: any): any {
    if (this.data?.chartConfig?.variant === 'sales-opportunity') {
      const chartDom = document.getElementById(this.data?.widgetId);
      if (!chartDom) return;

      if (!this.myChart) {
        this.myChart = echarts.init(chartDom, 'dvm');
      }

      this.myChart.showLoading();

      this.customConfigService
        .getConfig(this.data.apiUrl, this.data, {
          ...filters,
          useQueryParams: this.data?.useQueryParams || false,
        })
        ?.subscribe({
          next: (res: any) => {
            if (!res || (Array.isArray(res) && res.length === 0)) {
              this.hasError = true;
              this.errorMessage = 'No data available for the selected filters.';
              this.myChart.clear();
              this.myChart.hideLoading();
              return;
            }

            this.hasError = false;
            this.renderChart(res);
            this.addResizeListener();
            this.myChart.hideLoading();
          },
          error: () => {
            this.hasError = true;
            this.errorMessage = 'No Data available for the chart.';
            this.myChart.clear();
            this.myChart.hideLoading();
          },
        });

      return;
    }

    return super.loadAndRenderChart(filters);
  }

  protected override renderChart(chartData?: any): any {
    if (this.isSalesOpportunityChart(chartData)) {
      return this.renderSalesOpportunityChart(chartData);
    }

    return super.renderChart(chartData);
  }

  protected override setupCustomClickEvent(): any {
    // TODO: Add your custom logic here
    return super.setupCustomClickEvent();
  }

  protected override applyCategoryHighlight(categories?: any): any {
    // TODO: Add your custom logic here
    return super.applyCategoryHighlight(categories);
  }

  protected override resetCategoryHighlight(): any {
    // TODO: Add your custom logic here
    return super.resetCategoryHighlight();
  }

  protected override addResizeListener(): any {
    // TODO: Add your custom logic here
    return super.addResizeListener();
  }

  private isSalesOpportunityChart(chartData?: any): boolean {
    return this.data?.chartConfig?.variant === 'sales-opportunity' && !!chartData;
  }

  private renderSalesOpportunityChart(chartData: any): void {
    const variantConfig = this.data?.chartConfig || {};
    const source = this.resolveSalesOpportunitySource(chartData);
    const category = this.resolveCategory(source, variantConfig);
    const currentSales = this.readNumericValue(source, variantConfig.currentSalesKey, 'currentSales');
    const listPrice = this.readNumericValue(source, variantConfig.listPriceKey);
    const configuredOpportunity = this.readNumericValue(source, variantConfig.simulatedOpportunityKey);
    const simulatedOpportunity = configuredOpportunity || Math.max(listPrice - currentSales, 0);
    const configuredPercent = this.readNumericValue(source, variantConfig.simulatedOpportunityPercentKey);
    const simulatedOpportunityPercent = configuredPercent || (currentSales ? Math.round((simulatedOpportunity / currentSales) * 100) : 0);
    const currentColor = variantConfig.currentSalesColor || '#005f8f';
    const simulatedColor = variantConfig.simulatedOpportunityColor || '#718d16';
    const aggregateColor = variantConfig.aggregateColor || '#ffc000';
    const currentLabel = variantConfig.currentSalesLabel || 'Current Sales';
    const simulatedLabel = variantConfig.simulatedOpportunityLabel || 'Simulated\nOpp. ($)';
    const aggregateLabel = variantConfig.aggregateLabel || 'Aggregate\nOpp.';
    const yAxisName = variantConfig.yAxisName || category || 'Regional\nSales ($M)';
    const total = Math.max(currentSales + simulatedOpportunity, 1);

    this.allCategories = [category || yAxisName];

    const option = {
      backgroundColor: '#ffffff',
      grid: {
        left: variantConfig.gridLeft || 270,
        right: variantConfig.gridRight || 28,
        top: variantConfig.gridTop || 30,
        bottom: variantConfig.gridBottom || 24,
        containLabel: false,
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: () => [
          `${currentLabel.replace('\n', ' ')}: ${this.formatChartNumber(currentSales)}`,
          `${simulatedLabel.replace('\n', ' ')}: ${this.formatChartNumber(simulatedOpportunity)} (${simulatedOpportunityPercent}%)`,
        ].join('<br/>'),
      },
      xAxis: {
        type: 'value',
        show: false,
        max: Math.ceil(total * 1.04),
      },
      yAxis: {
        type: 'category',
        data: [category || yAxisName],
        axisTick: { show: false },
        axisLabel: { show: false },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#222222',
            width: 2,
          },
        },
        name: yAxisName,
        nameLocation: 'middle',
        nameGap: variantConfig.yAxisNameGap || 44,
        nameRotate: 90,
        nameTextStyle: {
          color: '#111111',
          fontSize: variantConfig.axisFontSize || 18,
          fontWeight: 400,
        },
      },
      graphic: [
        {
          type: 'rect',
          left: 24,
          top: 44,
          shape: { width: 28, height: 18 },
          style: { fill: currentColor },
        },
        {
          type: 'text',
          left: 64,
          top: 39,
          style: {
            text: currentLabel,
            fill: '#111111',
            fontSize: 18,
          },
        },
        {
          type: 'rect',
          left: 24,
          top: 86,
          shape: { width: 28, height: 18 },
          style: { fill: simulatedColor },
        },
        {
          type: 'text',
          left: 64,
          top: 76,
          style: {
            text: simulatedLabel,
            fill: '#111111',
            fontSize: 18,
            lineHeight: 22,
          },
        },
        {
          type: 'rect',
          left: 20,
          top: 142,
          shape: { width: 152, height: 58 },
          style: { fill: aggregateColor },
        },
        {
          type: 'text',
          left: 58,
          top: 147,
          style: {
            text: aggregateLabel,
            fill: '#000000',
            fontSize: 21,
            lineHeight: 25,
            align: 'center',
          },
        },
      ],
      series: [
        {
          name: currentLabel,
          type: 'bar',
          stack: 'sales',
          barWidth: variantConfig.barWidth || 86,
          data: [currentSales],
          itemStyle: { color: currentColor },
          label: {
            show: true,
            position: 'inside',
            color: '#ffffff',
            fontSize: variantConfig.valueFontSize || 20,
            formatter: ({ value }: any) => this.formatChartNumber(value),
          },
        },
        {
          name: simulatedLabel,
          type: 'bar',
          stack: 'sales',
          barWidth: variantConfig.barWidth || 86,
          data: [simulatedOpportunity],
          itemStyle: { color: simulatedColor },
          label: {
            show: true,
            position: 'inside',
            color: '#ffffff',
            fontSize: variantConfig.valueFontSize || 20,
            formatter: () => `${this.formatChartNumber(simulatedOpportunity)} (${simulatedOpportunityPercent}%)`,
          },
        },
      ],
    };

    this.myChart.setOption(option, true);
    this.myChart.hideLoading();
    this.wrapper.afterRender();
    this.setupCustomClickEvent();
  }

  private resolveSalesOpportunitySource(chartData: any): any {
    if (Array.isArray(chartData)) {
      return chartData[0] || {};
    }

    if (Array.isArray(chartData?.responseList?.[0]?.data)) {
      return chartData.responseList[0].data[0] || {};
    }

    if (Array.isArray(chartData?.data)) {
      return chartData.data[0] || {};
    }

    return chartData || {};
  }

  private resolveCategory(source: any, variantConfig: any): string {
    const categoryFields = Array.isArray(variantConfig.categoryFields) ? variantConfig.categoryFields : [];
    if (categoryFields.length > 0) {
      return categoryFields
        .map((field: string) => source?.[field])
        .filter((value: any) => value !== null && value !== undefined && value !== '')
        .join(' - ');
    }

    const categoryKey = variantConfig.categoryKey || 'category';
    return String(source?.[categoryKey] || variantConfig.yAxisName || '');
  }

  private readNumericValue(source: any, key?: string, fallbackKey?: string): number {
    const value = key ? source?.[key] : undefined;
    const fallbackValue = fallbackKey ? source?.[fallbackKey] : undefined;
    return Number(value ?? fallbackValue ?? 0);
  }

  private formatChartNumber(value: number): string {
    return formatDecimalWithCommas(Math.round(Number(value) || 0));
  }
}
